import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { AdsType, OrganisationDto } from "@ads/shared";
import { fetchOrganisationIds, fetchOrganisations } from "../api/organisations.js";
import { UploadProductTypeMenu } from "./UploadProductTypeMenu.js";
import { HelpGuide } from "./HelpGuide.js";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 150, 200];
const DEFAULT_PAGE_SIZE = 10;

export interface OrgListProps {
  /** Called when the user picks an org to author ads for (→ ad listing, US-C1). */
  onSelectOrg?: (org: OrganisationDto) => void;
  /** Called with the selected encoded id set + chosen product type to author one ad (US-D5). */
  onAuthorForSelected?: (orgIds: string[], adsType: AdsType) => void;
  /** Initial rows per page (default 10; user-adjustable via the footer selector). */
  pageSize?: number;
}

/** Debounce a fast-changing value (search box) so we don't query on every keystroke. */
function useDebounced<T>(value: T, ms = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

/** Page numbers to render, with `null` standing in for an ellipsis gap. */
function pageWindow(current: number, last: number): (number | null)[] {
  if (last <= 10) return Array.from({ length: last }, (_, i) => i + 1);
  const pages = new Set([1, last, current, current - 1, current + 1, current - 2, current + 2]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= last).sort((a, b) => a - b);
  const out: (number | null)[] = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) out.push(null);
    out.push(p);
    prev = p;
  }
  return out;
}

/**
 * Organisation picker (§7, US-B1/B2/B3). Searchable + paginated card list of every org,
 * mirroring the legacy admin look (Select All/Visible, count pill, circular pager). The
 * only per-org count is `defaultAdsCount` — the legacy summary-count N+1 stays out (§7).
 * No section tabs: this app authors Default Ads only.
 */
export function OrgList({
  onSelectOrg,
  onAuthorForSelected,
  pageSize: initialPageSize = DEFAULT_PAGE_SIZE,
}: OrgListProps) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const q = useDebounced(search.trim());

  // Any filter/page-size change returns to the first page so results stay coherent.
  useEffect(() => setPage(1), [q, pageSize]);

  const query = useQuery({
    queryKey: ["organisations", { q, page, pageSize }],
    queryFn: () => fetchOrganisations({ q: q || undefined, page, limit: pageSize, sort: "name", order: "asc" }),
    placeholderData: keepPreviousData,
  });

  const total = query.data?.total ?? 0;
  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  const visibleIds = useMemo(() => items.map((o) => o.id), [items]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  // "Select Visible" — only the orgs on the current page (the legacy default).
  function toggleVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  }

  // "Select All" — every org matching the current search, across ALL pages (US-B3).
  async function toggleSelectAll() {
    if (selected.size > 0) {
      setSelected(new Set());
      return;
    }
    const ids = await fetchOrganisationIds(q || undefined);
    setSelected(new Set(ids));
  }

  return (
    <>
      {/* This app authors Default Ads only, so no section tabs. Search sits with the
          product-type filter on the right; the checkboxes stay on the left. */}
      <div className="selectbar">
        <div className="select-toggles">
          <label className="checkline">
            <input type="checkbox" className="box" checked={selected.size > 0} onChange={toggleSelectAll} />
            Select All
          </label>
          <label className="checkline">
            <input type="checkbox" className="box" checked={allVisibleSelected} onChange={toggleVisible} />
            Select Visible
          </label>
        </div>
        <div className="select-right">
          <HelpGuide />
          <div className="org-search">
            <SearchIcon />
            <input
              placeholder="Search Organisation"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search organisation"
            />
            <span className="divider" />
            <span className="caret" aria-hidden><CaretIcon /></span>
          </div>
          <UploadProductTypeMenu
            disabled={selected.size === 0}
            onSelect={(adsType) => onAuthorForSelected?.([...selected], adsType)}
          />
        </div>
      </div>

      <div className="orglist">
        {query.isError && (
          <div className="org-row">
            <span className="error" role="alert">Could not load organisations.</span>
          </div>
        )}
        {!query.isError && items.length === 0 && (
          <div className="org-row">
            <span className="empty">{query.isLoading ? "Loading…" : "No organisations found."}</span>
          </div>
        )}
        {items.map((org) => (
          <div className="org-row" key={org.id}>
            <div className="org-id-cell">
              <input
                type="checkbox"
                className="box"
                checked={selected.has(org.id)}
                onChange={() => toggleOne(org.id)}
                aria-label={`Select ${org.name}`}
              />
              <button type="button" className="org-name" onClick={() => onSelectOrg?.(org)}>
                {org.name} [{org.custId}]
              </button>
            </div>
            <div className="pills">
              <span className="pill">Default Ads : {org.defaultAdsCount}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="list-foot">
        <label className="records">
          Records per page:
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} aria-label="Records per page">
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>

        <nav className="pager" aria-label="Pagination">
          <PageNav label="First page" disabled={page <= 1} onClick={() => setPage(1)}>«</PageNav>
          <PageNav label="Previous page" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</PageNav>
          {pageWindow(page, lastPage).map((p, i) =>
            p === null ? (
              <span key={`gap-${i}`} className="page-dot" aria-hidden>…</span>
            ) : (
              <button
                key={p}
                type="button"
                className={`page-dot${p === page ? " active" : ""}`}
                aria-label={`Page ${p}`}
                aria-current={p === page ? "page" : undefined}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            ),
          )}
          <PageNav label="Next page" disabled={page >= lastPage} onClick={() => setPage((p) => Math.min(lastPage, p + 1))}>›</PageNav>
          <PageNav label="Last page" disabled={page >= lastPage} onClick={() => setPage(lastPage)}>»</PageNav>
        </nav>
      </div>
    </>
  );
}

function PageNav({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button type="button" className="page-dot nav" aria-label={label} disabled={disabled} onClick={onClick}>
      {children}
    </button>
  );
}

function SearchIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function CaretIcon({ light = false }: { light?: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={light ? "#fff" : "currentColor"} strokeWidth="2.5" aria-hidden>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
